import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import AddressSearchManualStep, {
  type AddressSearchManualStepHandle,
} from "../../../common/AddressSearchManualStep";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  onContinue?: () => void;
  testID?: string;
};

export type { AddressSearchManualStepHandle as BillPayAddressStepHandle };

const BillPayAddressStep = forwardRef<AddressSearchManualStepHandle, Props>(
  ({ onValidationChange, onContinue, testID }, ref) => {
    const { t } = useTranslation();

    const gffpFieldsByCategory = useSessionStore(
      (s: SessionState) => (s as any).gffpFieldsByCategory
    );
    const senderInfoFields: { XmlTag?: string; xmlTag?: string; FieldLabel?: string; fieldLabel?: string }[] =
      gffpFieldsByCategory?.["Sender Information"] || [];

    const getFieldLabel = (tag: string): string | undefined => {
      const field = senderInfoFields.find(
        (f) => (f.XmlTag ?? f.xmlTag) === tag
      );
      return field?.FieldLabel ?? field?.fieldLabel;
    };

    const senderAddress =
      useSessionStore((s: SessionState) => (s as any).senderAddress) || "";
    const senderCity =
      useSessionStore((s: SessionState) => (s as any).senderCity) || "";
    const senderZipCode =
      useSessionStore((s: SessionState) => (s as any).senderZipCode) || "";
    const senderState =
      useSessionStore((s: SessionState) => (s as any).senderState) || "";
    const setSenderAddress = useSessionStore(
      (s: SessionState) => (s as any).setSenderAddress
    );
    const setSenderCity = useSessionStore(
      (s: SessionState) => (s as any).setSenderCity
    );
    const setSenderZipCode = useSessionStore(
      (s: SessionState) => (s as any).setSenderZipCode
    );
    const setSenderState = useSessionStore(
      (s: SessionState) => (s as any).setSenderState
    );

    const bindings = useMemo(
      () => ({
        address: senderAddress,
        city: senderCity,
        zipCode: senderZipCode,
        state: senderState,
        setAddress: (value: string) => setSenderAddress?.(value),
        setCity: (value: string) => setSenderCity?.(value),
        setZipCode: (value: string) => setSenderZipCode?.(value),
        setState: (value: string) => setSenderState?.(value),
      }),
      [
        senderAddress,
        senderCity,
        senderZipCode,
        senderState,
        setSenderAddress,
        setSenderCity,
        setSenderZipCode,
        setSenderState,
      ]
    );

    const labels = useMemo(
      () => ({
        line1: getFieldLabel("senderAddress") || t("ADDRESS"),
        line2: t("SENDER_ADDRESS_LINE2_PLACEHOLDER"),
        city:
          getFieldLabel("senderCity") || t("SENDER_ADDRESS_CITY_PLACEHOLDER"),
        postal:
          getFieldLabel("senderZipCode") ||
          t("SENDER_ADDRESS_POSTAL_PLACEHOLDER"),
        state:
          getFieldLabel("senderState") || t("SENDER_ADDRESS_STATE_PLACEHOLDER"),
      }),
      [gffpFieldsByCategory, t]
    );

    const testIds = useMemo(
      () => ({
        search: "billPayAddressSearch",
        suggestion: "billPayAddresResult",
        enterManually: "enterAddressManually",
        searchInstead: "searchAddressInstead",
        address: "billPayAddress",
        address2: "billPayAddress2",
        city: "billPayCity",
        zip: "billPayZipCode",
        state: "billPayState",
        stateOption: "billPayStateOption",
        continue: "continueButton",
      }),
      []
    );

    return (
      <AddressSearchManualStep
        ref={ref}
        testID={testID}
        bindings={bindings}
        labels={labels}
        testIds={testIds}
        onValidationChange={onValidationChange}
        onContinue={onContinue}
      />
    );
  }
);

BillPayAddressStep.displayName = "BillPayAddressStep";

export default BillPayAddressStep;
